import { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { getStringWidth } from '~/utils/string';

import type { TabProps } from './Tabs.types';

// Helper function to calculate the width of all tabs
const getTabsWidth = (tabListRef: React.RefObject<HTMLDivElement | null>) => {
  if (!tabListRef.current) {
    return 0;
  }
  return Array.from(tabListRef.current.children).reduce(
    (acc, child) => acc + (child as HTMLElement).getBoundingClientRect().width,
    0,
  );
};

const hideNextTab = (visibleTabs: Array<TabProps>, activeTabIndex: number) => {
  const hiddenTabsLength = visibleTabs.filter((tab) => tab.hidden).length;
  const visibleTabsLength = visibleTabs.length - hiddenTabsLength;
  const nextTabToHide = visibleTabs.findLast((tab, index) => !tab.hidden && index !== activeTabIndex);

  return visibleTabs.map((tab, index) => {
    if (index === activeTabIndex) {
      return tab;
    }
    if (nextTabToHide && nextTabToHide.value === tab.value && visibleTabsLength > 1) {
      return {
        ...tab,
        hidden: true,
      };
    }
    return tab;
  });
};

const showTab = (visibleTabs: Array<TabProps>, tabToShow: TabProps) =>
  visibleTabs.map((tab) => {
    if (tab.value === tabToShow.value) {
      return {
        ...tab,
        hidden: false,
      };
    }
    return tab;
  });

export function useTabGroup({ tabs, selectedTab }: { tabs: Array<TabProps>; selectedTab?: string }) {
  const tabListRef = useRef<HTMLDivElement | null>(null);
  const initialTabWidth = useRef<number | null>(null);
  const { width: windowSize } = useWindowSize({ debounceDelay: 200 });

  const [visibleTabs, setVisibleTabs] = useState<Array<TabProps>>(tabs);
  const [prevTabs, setPrevTabs] = useState(tabs);
  if (tabs !== prevTabs) {
    setPrevTabs(tabs);
    setVisibleTabs(tabs);
  }

  useEffect(() => {
    if (windowSize === null || tabListRef.current === null) {
      return;
    }

    if (initialTabWidth.current === null) {
      const tabWidth = getTabsWidth(tabListRef);
      if (tabWidth) {
        initialTabWidth.current = tabWidth;
      }
    }

    if (initialTabWidth.current === null) {
      return;
    }

    const visibleTabsCount = visibleTabs.filter((tab) => !tab.hidden).length;

    const tabListWidth = tabListRef.current.getBoundingClientRect().width;
    const tabsWidth = getTabsWidth(tabListRef);
    const activeTabIndex = tabs.findIndex((tab) => tab.value === selectedTab);

    if (initialTabWidth.current > tabListWidth && visibleTabsCount > 1) {
      if (tabsWidth > tabListWidth) {
        setVisibleTabs(hideNextTab(visibleTabs, activeTabIndex));
        return;
      }
    }

    const firstHiddenTab = visibleTabs.find((tab) => tab.hidden);
    if (firstHiddenTab) {
      const fakeTabWidth = getStringWidth(firstHiddenTab.label, [
        'button-100',
        'text-nowrap',
        'px-5',
        'h-[40px]',
        'text-[15px]',
        'leading-none',
      ]);

      if (tabsWidth + fakeTabWidth < tabListWidth) {
        setVisibleTabs(showTab(visibleTabs, firstHiddenTab));
      }
    }
  }, [tabs, selectedTab, visibleTabs, windowSize]);

  const handleSelectedTabChange = (t: string) => {
    const newVisibleTabs = visibleTabs.map((tab) => {
      if (tab.value === t) {
        return {
          ...tab,
          hidden: false,
        };
      }
      return tab;
    });
    setVisibleTabs(newVisibleTabs);
  };

  return { handleSelectedTabChange, tabListRef, visibleTabs };
}
